import { useEffect, useState } from "react";
import { NewRunnerForm, PairCodeForm, PairEndCard, PairRequestCard, RunnerTable, RunnerTokenCard, runnerState } from "../components/Runner";
import { Button } from "../components/Button";
import { Alert, Loading } from "../components/Card";
import { AuthError, authApi, authErrText } from "../lib/auth";
import type { RunnerPairRequest, RunnerRow } from "../lib/auth";
import { useRouteParam } from "../lib/route";
import { tx } from "../i18n";

/* ══ 설정 › 러너(관리자만) — 채택 시안 A안(Figma 15-3) ══
   표 하나 + 위에 펼치는 추가 폼 · 한 번만 보이는 토큰 카드. 목록은 열 때 한 번 읽고, 발급 · 폐기 뒤에
   다시 읽는다 — 타이머로 박동하지 않는다(붙음 상태는 새로고침으로 본다).
   페어링(시안 Figma 19-2)이 첫 길이다 — 서버가 띄운 주소(`?pair=<코드>`)로 오거나 코드를 넣으면 요청
   카드가 선다. 「토큰 직접 발급」은 보조 길로 남는다. */

type Pair =
  | { at: "loading"; code: string }
  | { at: "ask"; req: RunnerPairRequest }
  | { at: "end"; kind: "approved" | "denied" | "missing"; name?: string };
export function RunnersSection() {
  const [rows, setRows] = useState<RunnerRow[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [issued, setIssued] = useState<{ name: string; token: string } | null>(null);
  const [busyName, setBusyName] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<string | null>(null);
  const linked = useRouteParam("pair");
  const [pair, setPair] = useState<Pair | null>(null);
  const [pairBusy, setPairBusy] = useState<"approve" | "deny" | null>(null);
  const [pairErr, setPairErr] = useState<string | null>(null);
  const [takenWeb, setTakenWeb] = useState<string | null>(null);

  function load() {
    authApi
      .runners()
      .then((r) => {
        setRows(r);
        setLoadErr(null);
      })
      .catch((e) => setLoadErr(authErrText(e)));
  }
  useEffect(load, []);

  function openPair(code: string) {
    setPair({ at: "loading", code });
    setPairErr(null);
    setTakenWeb(null);
    authApi
      .pairRequest(code)
      .then((req) =>
        // 이미 허용한 요청을 다시 열었으면(주소를 또 누름) 끝 카드로 — 두 번 허용할 일이 없다.
        setPair(req.approvedName ? { at: "end", kind: "approved", name: req.approvedName } : { at: "ask", req }),
      )
      .catch((e) => {
        if (e instanceof AuthError && e.status === 404) setPair({ at: "end", kind: "missing" });
        else {
          setPair(null);
          setLoadErr(authErrText(e));
        }
      });
  }
  useEffect(() => {
    if (linked) openPair(linked);
  }, [linked]);

  function closePair() {
    setPair(null);
    // 주소의 코드를 지운다 — 남겨 두면 새로고침마다 끝난 요청을 다시 연다.
    if (linked) location.hash = "#/settings?tab=runners";
  }

  async function approve(code: string, name: string, replace: boolean) {
    setPairBusy("approve");
    setPairErr(null);
    try {
      await authApi.approvePair(code, name, replace);
      setPair({ at: "end", kind: "approved", name });
      load();
    } catch (e) {
      if (e instanceof AuthError && e.status === 404) setPair({ at: "end", kind: "missing" });
      // 웹 러너와 겹치면 문장 대신 「바꿔서 허용」을 세운다 — 그 카드가 무엇이 사라지는지 말한다.
      else if (e instanceof AuthError && e.code === "runner exists") setTakenWeb(name);
      else setPairErr(authErrText(e));
    } finally {
      setPairBusy(null);
    }
  }

  async function deny(code: string) {
    setPairBusy("deny");
    setPairErr(null);
    try {
      await authApi.denyPair(code);
      setPair({ at: "end", kind: "denied" });
    } catch (e) {
      if (e instanceof AuthError && e.status === 404) setPair({ at: "end", kind: "missing" });
      else setPairErr(authErrText(e));
    } finally {
      setPairBusy(null);
    }
  }

  async function create(name: string) {
    setCreating(true);
    setCreateErr(null);
    try {
      const r = await authApi.createRunner(name);
      setIssued(r);
      setAdding(false);
      load();
    } catch (e) {
      setCreateErr(authErrText(e));
    } finally {
      setCreating(false);
    }
  }

  async function revoke(name: string) {
    setBusyName(name);
    setRowErr(null);
    try {
      await authApi.revokeRunner(name);
      load();
    } catch (e) {
      setRowErr(authErrText(e));
    } finally {
      setBusyName(null);
    }
  }

  const live = rows?.filter((r) => runnerState(r) === "live").length ?? 0;
  return (
    <>
      <h2>{tx("runners.title")}</h2>
      <p className="muted">{tx("runners.lead")}</p>
      {loadErr ? <Alert tone="error">{tx("runners.loadFailed", { err: loadErr })}</Alert> : null}
      <div className="rbar">
        <span className="rbar__sum">{rows ? tx("runners.summary", { n: rows.length, live }) : null}</span>
        <div className="rbar__tools">
          <PairCodeForm onOpen={openPair} />
          {!adding && !issued ? <Button onClick={() => setAdding(true)}>{tx("runners.add")}</Button> : null}
        </div>
      </div>
      {pair?.at === "loading" ? <Loading text={tx("runners.pair.loading")} /> : null}
      {pair?.at === "ask" ? (
        <PairRequestCard
          key={pair.req.code}
          req={pair.req}
          busy={pairBusy}
          error={pairErr}
          takenWeb={takenWeb}
          onApprove={(name, replace) => approve(pair.req.code, name, replace)}
          onDeny={() => deny(pair.req.code)}
        />
      ) : null}
      {pair?.at === "end" ? <PairEndCard kind={pair.kind} name={pair.name} onClose={closePair} /> : null}
      {issued ? <RunnerTokenCard name={issued.name} token={issued.token} onClose={() => setIssued(null)} /> : null}
      {adding ? (
        <NewRunnerForm
          busy={creating}
          error={createErr}
          onSubmit={create}
          onCancel={() => {
            setAdding(false);
            setCreateErr(null);
          }}
        />
      ) : null}
      {rows ? <RunnerTable rows={rows} busyName={busyName} error={rowErr} onRevoke={revoke} /> : !loadErr ? <Loading text={tx("runners.loading")} /> : null}
    </>
  );
}
